/* eslint-disable */
// @ts-nocheck
import * as R from 'ramda';
import deepFreeze from './deepFreeze';
import m from 'mori';
import moment from 'moment';
import crypto from 'crypto';
import { htmlEncoder } from '../../../../shared/utils';
import { FILTER_DIMENSIONS, PLATFORMS, DRUID_TO_HUMAN_READABLE } from '../../common/support/eo_mapping';
import { convertHumanReadableToDruid } from '../../common/support/utils/eo_helpers';
import { LOADING_STATUS, MAU_BILLING_TIER_IDS, MAU_BILLING_TIERS, MULTI_PRODUCT_DISCOUNT_SKU } from './constants';
import * as adPartnerTypes from '../types/adPartnerTypes';
import type { View } from '../../common/types/appTypes';
import type { Subscription, SubscriptionReducer } from '../types/subscriptionTypes';

/**
 * Returns a hashed version of the device id based on hash type
 * @param {String} hashType - sha1 or md5
 * @param {String} id - device id
 */
export const getHashedId = (hashType: string, id: string): string => {
  return crypto.createHash(hashType).update(id).digest('hex');
};
export const reduceObj = R.curry(function (reduction, acc, obj) {
  return R.reduce(
    function (a, key) {
      return reduction(a, obj[key], key);
    },
    acc,
    R.keys(obj)
  );
});

/* Legacy Monkey Patch -- use util ref instead */
R.reduceObj = reduceObj;
export const removeClass = function removeClass(el: Record<string, any>, className: string) {
  if (el.classList) {
    el.classList.remove(className);
  } else {
    el.className = el.className.replace(new RegExp('(^|\\b)' + className.split(' ').join('|') + '(\\b|$)', 'gi'), ' ');
  }
};

/* check whether the path is a relative path */
export const pathIsRelative = function pathIsRelative(path: string) {
  const relativePathRegex = /^(\/$|\/(\?_)?[a-zA-Z0-9]+)/g; // Fix AppSec-193 (Jour-3765): Test Case //google.com /\\google.com /%09/google.com

  if (path && path.match(relativePathRegex)) {
    return true;
  }

  return false;
};
export const hasClass = function hasClass(el: Record<string, any>, className: string) {
  if (el.classList) {
    return el.classList.contains(className);
  } else {
    return new RegExp('(^| )' + className + '( |$)', 'gi').test(el.className);
  }
};
export const addClass = function addClass(el: Record<string, any>, className: string) {
  if (el.classList) {
    el.classList.add(className);
  } else {
    el.className += ' ' + className;
  }
};
export const cx = R.pipe(R.pickBy(R.identity), R.keys, R.join(' '));
export const roundUpNice = function (x: number) {
  var pow = Math.pow(10, Math.floor(Math.log(x) / Math.log(10)));
  return Math.ceil(x / pow) * pow;
};
export const debounce = function (func: (...args: any) => void, wait: number, immediate?: boolean) {
  var timeout;

  function bouncer() {
    var self = this;
    var args = arguments;

    var later = function () {
      timeout = null;

      if (!immediate) {
        func.apply(self, args);
      }
    };

    var callNow = immediate && !timeout;
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);

    if (callNow) {
      func.apply(self, args);
    }
  }

  bouncer.cancel = () => {
    if (timeout) {
      clearTimeout(timeout);
    }
  };

  return bouncer;
};
export const takeLatest = (func: any, delay: number) => {
  let takeTimer;
  return function () {
    var self = this;
    const args = arguments;
    clearTimeout(takeTimer);
    takeTimer = setTimeout(() => func.apply(self, args), delay);
  };
};
export const takeLeading = (func: any) => {
  let activeRequest = false;
  return async function () {
    if (activeRequest) {
      return;
    }

    activeRequest = true;
    let res;

    try {
      res = await func();
      activeRequest = false;
    } catch (error) {
      activeRequest = false;
      throw error;
    }

    return res;
  };
};

/* NUMBER FORMATERS */

/* roundToFixed2(.5) -> 0.5 roundToFixed2(4.53333) -> 4.53 */
export const roundToFixed2 = function (num: number) {
  return Math.round(num * 100) / 100;
};
export const roundToFixedN = function (n: number, num: number): number {
  let base = 1;

  for (var i = 1; i <= n; i++) {
    base *= 10;
  }

  return Math.round(num * base) / base;
};
export const forceToFixed2 = function (num: number) {
  return (Math.round(num * 100) / 100).toFixed(2);
};
export const roundToFixed0 = function (num: number) {
  return Math.round(num);
};
export const formatWholeValueWithThousandsSeparator = function (wholeValue: string) {
  return wholeValue.replace(/./g, function (c, i, a) {
    return i && (a.length - i) % 3 === 0 ? ',' + c : c;
  });
};
export const formatValueWithThousandsSeparator = function (value: string) {
  return value.replace(/^[\d]+/, function (wholeValue) {
    return formatWholeValueWithThousandsSeparator(wholeValue);
  });
};
// Only support USD localization for now
export const formatStringToDollar = function (str: string, currency: string = '') {
  if (typeof str !== 'string') {
    throw 'Argument str is not a valid type, expects "string", got ' + typeof str;
  }

  return currency + formatValueWithThousandsSeparator(str);
};
export const commas = function (s: number | string) {
  if (!s) {
    s = 0;
  }

  return s.toLocaleString();
};
export const safeLocaleString = function (num: number) {
  if (!num) {
    num = 0;
  }

  return num.toLocaleString();
};
export function formatBytes(bytes: number, decimals: number) {
  if (bytes === 0) {
    return '0 Bytes';
  }

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
}
// http://stackoverflow.com/questions/9461621/how-to-format-a-number-as-2-5k-if-a-thousand-or-more-otherwise-900-in-javascrip
export const formatNumber = function (num: number) {
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1).replace(/\.0$/, '') + 'm';
  }

  if (num >= 1000) {
    return (num / 1000).toFixed(1).replace(/\.0$/, '') + 'k';
  }

  return num;
};
export const clickedOutsideElement = function (element: Record<string, any>, event: Record<string, any>) {
  var eventTarget = event.target || event.srcElement;

  while (eventTarget !== null) {
    if (eventTarget === element) {
      return false;
    }

    eventTarget = eventTarget.parentNode;
  }

  return true;
};
export function toSelectFormat(list: Array<any>, metadata?: {}) {
  if (!metadata) {
    metadata = {};
  }

  return list.map((val: string) => {
    return Object.assign(
      {
        label: val,
        value: val,
      },
      metadata
    );
  });
}
export const toHeaderFormat = (headers: Array<string>, labelMap: Record<string, any> = DRUID_TO_HUMAN_READABLE) => {
  return headers.map((val) => ({
    display: labelMap[val] || val,
    key: val,
  }));
};
export const toPercentageStr = function (num: number) {
  return `${commas(num * 100)}%`;
};
// precision parameter specifies number of decimal places desired
export const castToDecimalPrecisePercentageStr = (num: number, precision: number) => {
  const str = (num * 100).toFixed(precision);
  return `${str}%`;
};
export const strToTitleCase = (str: string) => {
  return str ? str.slice(0, 1).toUpperCase().concat(str.slice(1).toLowerCase()) : '';
};
export const toJsAll = R.mapObjIndexed(m.toJs);
export const downloadCSV = function (data: string, title: string = 'download') {
  const link = document.createElement('a');
  const filename = `${title}.csv`;
  const mime = 'data:attachment/csv;charset=utf-8,';
  var blob = new Blob([data], {
    type: mime,
  });

  if (window.navigator.msSaveOrOpenBlob) {
    window.navigator.msSaveBlob(blob, filename);
  } else {
    link.setAttribute('href', window.URL.createObjectURL(blob));
    link.setAttribute('download', filename);
    link.setAttribute('target', '_blank');
    link.style.display = 'none';
    window.document.body.appendChild(link);
    link.click();
    window.document.body.removeChild(link);
  }
};
export const downloadFile = function (href: any, filename: string) {
  const link = document.createElement('a');
  link.setAttribute('href', href);
  link.setAttribute('download', filename);
  link.setAttribute('target', '_blank');
  link.setAttribute('rel', 'noopener noreferrer');
  window.document.body.appendChild(link);
  link.click();
  window.document.body.removeChild(link);
};
export const daysBetween = function (start: string, end: string) {
  start = moment(start);
  end = moment(end);
  // if today is included in the range round down instead of up
  const opp = moment().startOf('day').isBetween(start, end, undefined, '(]') ? Math.floor : Math.ceil;
  const duration = moment.duration(moment(end).diff(start));
  return opp(duration.asDays());
};
export const reducerWrapper = function <H extends Record<string, any>, A extends Record<string, any>>(
  init_state: H | undefined,
  handler: (state: H, action: A) => H,
  options: Record<string, any>
) {
  return function (state: H | undefined, action: A) {
    if (!state) {
      state = init_state;
    }

    if (options) {
      if (options.request && options.request.length) {
        for (const action_group of options.request) {
          if (action_group.request == action.type) {
            return R.merge(state, {
              loadingStatus: LOADING_STATUS.LOADING,
              loading: true,
              error: null,
            });
          }
        }
      }

      if (options.error && options.error.length) {
        for (const action_group of options.error) {
          if (action_group.error == action.type) {
            return R.merge(state, {
              loadingStatus: LOADING_STATUS.ERROR,
              loading: false,
              error: action.error,
            });
          }
        }
      }

      // i.e. 'APP_CHANGE'
      if (options.resetOn) {
        for (const reset_action_type of options.resetOn) {
          if (reset_action_type == action.type) {
            return init_state;
          }
        }
      }
    }

    return handler(state, action);
  };
};
export const generateConstants = function (str: string, extra_methods: Array<string> | null | undefined = []) {
  const actions = R.concat(['request', 'response', 'error'], extra_methods);
  return deepFreeze(
    R.reduce(
      (acc, action) => {
        return R.assoc(action, `${str.toUpperCase()}_${action.toUpperCase()}`, acc);
      },
      {},
      actions
    )
  );
};
export class ReduxAsyncAction {
  constants: Record<string, any>;

  progressBar: boolean;

  request: (...args: any) => Record<string, any>;

  dispatch: (action: Record<string, any>) => Record<string, any>;

  constructor(action_namespace: string, extra_actions?: Array<string> | null | undefined) {
    this.constants = generateConstants(action_namespace, extra_actions);
    this.progressBar = false;
  }

  showProgressBar() {
    this.progressBar = true;
  }

  registerRequest(promiseClosure: (...args: any) => Record<string, any>) {
    this.request = (...args) => {
      return (dispatch) => {
        dispatch({
          type: this.constants.request,
        });

        if (this.progressBar) {
          dispatch({
            type: 'PROGRESS_BAR_ACTIVE',
          });
        }

        this.dispatch = dispatch;
        return promiseClosure
          .apply(this, args)
          .then((data) => {
            dispatch({
              type: this.constants.response,
              data,
            });

            if (this.progressBar) {
              dispatch({
                type: 'PROGRESS_BAR_COMPLETE',
              });
            }

            return data;
          })
          .catch((error) => {
            const { response } = error;
            dispatch({
              type: this.constants.error,
              error: response ? R.pick(['data', 'status', 'statusText'], response) || response : 'Unknown error',
            });

            if (this.progressBar) {
              dispatch({
                type: 'PROGRESS_BAR_ERROR',
              });
            }

            throw response || error;
          });
      };
    };
  }
}
export const isStoreDataLoading = function (stores: Record<string, any>) {
  return R.reduce(
    (isLoading, store) => {
      return isLoading || store.loading || !store.data;
    },
    false,
    stores
  );
};
export const isActiveSubscription = function (val: string) {
  return R.contains(val, ['Active', 'Grace', 'Trial']);
};
export const isActiveSubscriptionFromList = function (sku: string, sub_list: Array<Record<string, any>>) {
  if (!sub_list) {
    return false;
  }

  const sub = R.find(R.propEq('sku', sku), sub_list);

  if (!sub) {
    return false;
  }

  return isActiveSubscription(sub.status);
};
export function shouldRerouteOnInactiveSubscription(subscriptions: Record<string, any> = {}, sku: string): boolean {
  const { data, loading } = subscriptions;

  if (!loading && data && data.length) {
    return !isActiveSubscriptionFromList(sku, data);
  }

  return false;
}
export function shouldRerouteOnActiveSubscription(subscriptions: Record<string, any> = {}, sku: string): boolean {
  return !shouldRerouteOnInactiveSubscription(subscriptions, sku);
}
export function getActiveMauProducts(products: Array<Record<string, any>> = []) {
  return products.filter((product) => {
    if (
      product.status !== 'Inactive' &&
      product.tiers &&
      product.tiers.find((tier) => tier && tier.enabled && tier.branch_mau_tier)
    ) {
      return true;
    }

    return false;
  });
}
export function getDiscountProducts(products: Array<Record<string, any>> = []) {
  return products.filter((product) => {
    return product.sku === MULTI_PRODUCT_DISCOUNT_SKU && product.status !== 'Inactive';
  });
}

/**
 * Get branch mau tier for the app from the given subscriptions
 * @param  {Object} subscriptions
 * @return {string} branch mau tier if it exists otherwise null
 */
export function getBranchMauTier(subscriptions: SubscriptionReducer): string | null | undefined {
  if (subscriptions.data) {
    const activeProducts = subscriptions.data.filter((product) => product && product.status !== 'Inactive');
    return (
      activeProducts &&
      activeProducts.reduce((acc, product: Subscription) => {
        if (acc) {
          return acc;
        }

        const tier = product.tiers && product.tiers.find((tier) => tier && tier.enabled && tier.branch_mau_tier);
        return tier && tier.branch_mau_tier;
      }, null)
    );
  }

  return null;
}
export function getMauTierObject(tierId: string | null | undefined, tiers: Array<Record<string, any>> | null | undefined) {
  return (tiers || MAU_BILLING_TIERS).find((tier) => tier.tier_id === tierId);
}
export function getTierRecommendation(mau: number = 0, currentTier: Record<string, any>) {
  if (currentTier && currentTier.tier_id === MAU_BILLING_TIER_IDS.ENTERPRISE) {
    return MAU_BILLING_TIERS.find((tier) => tier.tier_id === MAU_BILLING_TIER_IDS.ENTERPRISE);
  }

  return MAU_BILLING_TIERS.find((tier) => tier.mau_num > mau);
}
export const convertToOrdinalNumber = function (num: number) {
  switch (num) {
    case 1:
      return `${num}st`;

    case 2:
      return `${num}nd`;

    case 3:
      return `${num}rd`;

    default:
      return `${num}st`;
  }
};
export const tryJSONParse = function (json: string, defualt_res: Record<string, any> | null | undefined = null) {
  try {
    return JSON.parse(json);
  } catch (e) {
    return defualt_res;
  }
};

/* MORI HELPERS */
export const sumM = function (keys: Array<string>, m_obj: Record<string, any>) {
  return R.reduce(
    function (acc, key) {
      return acc + (m.get(m_obj, key) || 0);
    },
    0,
    keys
  );
};

/* MATHS */
export const getUpperBound = function (max: number, divisions: number) {
  if (!max) {
    return 0;
  }

  var pow = Math.pow(divisions, Math.floor(Math.log(max) / Math.log(divisions)));
  var nice = Math.ceil(max / pow) * pow;
  return Math.ceil(nice / divisions) * divisions;
};
// Safely divide when the denominator is 0 so that Infinity isn't returned
export const divideSafe = function (a: number, b: number) {
  if (!b) {
    return 0;
  }

  return a / b;
};

/* ANALYTICS */
export const hasEvents = R.reduceObj((acc, val, key) => {
  if (acc) {
    return acc;
  }

  return /(open|install)/.test(key) && !!val;
}, false);
const CF_CLICK = '(bnc`click|deepview`view|smartbanner`click)$';
export const metrics = {
  ALL_INSTALL: '.*`install(_assist)?$',
  ALL_OPEN: '.*`open$',
  BRANCH_INSTALL: '.*`(ios|android)`.*`install$',
  // excludes OTHER_INSTALL
  BRANCH_OPEN: '.*`(ios|android)`.*`open$',
  // excludes OTHER_OPEN
  // Total of all clicks represented on the clickflow. Excludes all
  // deepview clicks as well as all other mobile clicks (i.e. amazon / windows phone)
  ALL_CF_CLICK: '(mac|windows|other|NULL)`(ios|android|NULL)`' + CF_CLICK,
  // this excludes deepview clicks but includes desktop clicks thet ended up in mobile clicks on android
  ALL_IOS_CLICK: '.*`ios`' + CF_CLICK,
  ALL_IOS_INSTALL: '.*`ios`.*`install$',
  ALL_IOS_OPEN: '.*`ios`.*`open$',
  // this excludes deepview clicks but includes desktop clicks thet ended up in mobile clicks on android
  ALL_ANDROID_CLICK: '.*`android`' + CF_CLICK,
  ALL_ANDROID_INSTALL: '.*`android`.*`install$',
  ALL_ANDROID_OPEN: '.*`android`.*`open$',

  /* METRICS USED IN CLICK FLOW */
  DESKTOP_CLICK: '(mac|windows|other)`NULL`' + CF_CLICK,
  DESKTOP_TEXTED: '(mac|windows|other)`NULL`.*`texted$',
  DESKTOP_IOS_CLICK: '(mac|windows|other)`ios`(bnc`click|deepview`view|deepview`click|smartbanner`click)$',
  DESKTOP_ANDROID_CLICK: '(mac|windows|other)`android`(bnc`click|deepview`view|deepview`click|smartbanner`click)$',
  DESKTOP_IOS_INSTALL: '(mac|windows|other)`ios`.*`install$',
  DESKTOP_IOS_OPEN: '(mac|windows|other)`ios`.*`open$',
  DESKTOP_ANDROID_INSTALL: '(mac|windows|other)`android`.*`install$',
  DESKTOP_ANDROID_OPEN: '(mac|windows|other)`android`.*`open$',
  MOBILE_IOS_CLICK: 'NULL`ios`' + CF_CLICK,
  MOBILE_IOS_INSTALL: 'NULL`ios`.*`install$',
  MOBILE_IOS_OPEN: 'NULL`ios`.*`open$',
  MOBILE_ANDROID_CLICK: 'NULL`android`' + CF_CLICK,
  MOBILE_ANDROID_INSTALL: 'NULL`android`.*`install$',
  MOBILE_ANDROID_OPEN: 'NULL`android`.*`open$',

  /* DEEPVIEWS */
  MOBILE_IOS_DEEPVIEW_SERVED: 'NULL`ios`deepview`view$',
  MOBILE_ANDROID_DEEPVIEW_SERVED: 'NULL`android`deepview`view$',
  DESKTOP_DEEPVIEW_SERVED: '(mac|windows|other)`NULL`deepview`view$',
  MOBILE_IOS_DEEPVIEW_CLICK: 'NULL`ios`deepview`click$',
  MOBILE_ANDROID_DEEPVIEW_CLICK: 'NULL`android`deepview`click$',
  DESKTOP_DEEPVIEW_CLICK: '(mac|windows|other)`.*`deepview`click$',
  IOS_DEEPVIEW_INSTALL: '.*`ios`deepview`install$',
  ANDROID_DEEPVIEW_INSTALL: '.*`android`deepview`install$',
  IOS_DEEPVIEW_OPEN: '.*`ios`deepview`open$',
  ANDROID_DEEPVIEW_OPEN: '.*`android`deepview`open$',

  /* SMART BANNER */
  MOBILE_IOS_SMARTBANNER_SERVED: 'NULL`ios`smartbanner`view$',
  MOBILE_ANDROID_SMARTBANNER_SERVED: 'NULL`android`smartbanner`view$',
  DESKTOP_SMARTBANNER_SERVED: '(mac|windows|other)`NULL`smartbanner`view$',
  MOBILE_IOS_SMARTBANNERR_CLICK: 'NULL`ios`smartbanner`click$',
  MOBILE_ANDROID_SMARTBANNER_CLICK: 'NULL`android`smartbanner`click$',
  DESKTOP_SMARTBANNER_CLICK: '(mac|windows|other)`.*`smartbanner`click$',
  IOS_SMARTBANNER_INSTALL: '.*`ios`smartbanner`install$',
  ANDROID_SMARTBANNER_INSTALL: '.*`android`smartbanner`install$',
  IOS_SMARTBANNER_OPEN: '.*`ios`smartbanner`open$',
  ANDROID_SMARTBANNER_OPEN: '.*`android`smartbanner`open$',
};
deepFreeze(metrics);
export const clickflow_metrics = {
  desktop_click: metrics.DESKTOP_CLICK,
  desktop_texted: metrics.DESKTOP_TEXTED,
  desktop_ios_click: metrics.DESKTOP_IOS_CLICK,
  desktop_android_click: metrics.DESKTOP_ANDROID_CLICK,
  desktop_ios_install: metrics.DESKTOP_IOS_INSTALL,
  desktop_ios_open: metrics.DESKTOP_IOS_OPEN,
  desktop_android_install: metrics.DESKTOP_ANDROID_INSTALL,
  desktop_android_open: metrics.DESKTOP_ANDROID_OPEN,
  mobile_ios_click: metrics.MOBILE_IOS_CLICK,
  mobile_ios_install: metrics.MOBILE_IOS_INSTALL,
  mobile_ios_open: metrics.MOBILE_IOS_OPEN,
  mobile_android_click: metrics.MOBILE_ANDROID_CLICK,
  mobile_android_install: metrics.MOBILE_ANDROID_INSTALL,
  mobile_android_open: metrics.MOBILE_ANDROID_OPEN,
};
deepFreeze(clickflow_metrics);
export const deepview_smartbanner_metrics = {
  /* DEEPVIEWS */
  mobile_ios_deepview_served: metrics.MOBILE_IOS_DEEPVIEW_SERVED,
  mobile_android_deepview_served: metrics.MOBILE_ANDROID_DEEPVIEW_SERVED,
  desktop_deepview_served: metrics.DESKTOP_DEEPVIEW_SERVED,
  mobile_ios_deepview_click: metrics.MOBILE_IOS_DEEPVIEW_CLICK,
  mobile_android_deepview_click: metrics.MOBILE_ANDROID_DEEPVIEW_CLICK,
  desktop_deepview_click: metrics.DESKTOP_DEEPVIEW_CLICK,
  ios_deepview_install: metrics.IOS_DEEPVIEW_INSTALL,
  android_deepview_install: metrics.ANDROID_DEEPVIEW_INSTALL,
  ios_deepview_open: metrics.IOS_DEEPVIEW_OPEN,
  android_deepview_open: metrics.ANDROID_DEEPVIEW_OPEN,

  /* SMART BANNER */
  mobile_ios_smartbanner_served: metrics.MOBILE_IOS_SMARTBANNER_SERVED,
  mobile_android_smartbanner_served: metrics.MOBILE_ANDROID_SMARTBANNER_SERVED,
  desktop_smartbanner_served: metrics.DESKTOP_SMARTBANNER_SERVED,
  mobile_ios_smartbanner_click: metrics.MOBILE_IOS_SMARTBANNERR_CLICK,
  mobile_android_smartbanner_click: metrics.MOBILE_ANDROID_SMARTBANNER_CLICK,
  desktop_smartbanner_click: metrics.DESKTOP_SMARTBANNER_CLICK,
  ios_smartbanner_install: metrics.IOS_SMARTBANNER_INSTALL,
  android_smartbanner_install: metrics.ANDROID_SMARTBANNER_INSTALL,
  ios_smartbanner_open: metrics.IOS_SMARTBANNER_OPEN,
  android_smartbanner_open: metrics.ANDROID_SMARTBANNER_OPEN,
};
deepFreeze(deepview_smartbanner_metrics);
export const journey_metrics = {
  /* JOURNEYS */

  /* No longer supporting Aerospike queries */
  ios_journey_served: '',
  ios_journey_click: '',
  ios_journey_install: '',
  ios_journey_open: '',
  android_journey_served: '',
  android_journey_click: '',
  android_journey_install: '',
  android_journey_open: '',
  private_journey_served: '',
};
deepFreeze(journey_metrics);

/* DATES */
// NOTE: This whole DATES section is deprecated, please use dateUtils instead.
// TODO: Can be removed once EO is fully on and non-EO pages deprecated.
const DEFAULT_DATE_FORMAT = 'YYYY-MM-DDTHH:mm:ssZ';
export const date = {
  getFirstMoment(date: string, format: string) {
    return moment(date)
      .hour(0)
      .minute(0)
      .second(0)
      .millisecond(0)
      .format(format || DEFAULT_DATE_FORMAT);
  },

  getLastMoment(date: string, format: string) {
    return moment(date)
      .hour(23)
      .minute(59)
      .second(59)
      .millisecond(999)
      .format(format || DEFAULT_DATE_FORMAT);
  },

  getRange(duration: number, granularity: string = 'days') {
    return {
      start_date: this.getFirstMoment(moment().subtract(duration, granularity)),
      end_date: this.getLastMoment(),
    };
  },
};

/* RANDO UTILS */
export const renameProp = function (from: string, to: string, data: Record<string, any>) {
  if (!data) {
    return data;
  }

  return R.pipe(R.assoc(to, data[from]), R.omit([from]))(data);
};
export const removeFilter = function (key: string, value: string, filters: Record<string, any>) {
  filters = R.assoc(key, R.reject(R.equals(value), filters[key]), filters);

  if (filters[key] && !filters[key].length) {
    return R.omit([key], filters);
  }

  return filters;
};
export const selectFilter = function (key: string, value: string, filters: Record<string, any>) {
  if (!filters[key]) {
    return R.assoc(key, [value], filters);
  }

  return R.assoc(key, R.append(value, filters[key]), filters);
};
// clean
export const filterOptionsCleanup = R.pipe(R.omit(['all']), (filters) => {
  if (!filters.os) {
    return filters;
  }

  return R.assoc('os', R.reject(R.equals('robots'), filters.os), filters);
});
export const sortRows = function (
  hash: string | null | undefined,
  headers: Array<Record<string, any>>,
  header_key: string, // for default comparator, forward means alphabetically as strings:
  // '01-foo'
  // '10-foo'
  // '1-foo'
  // '8-foo'
  // 'A-foo'
  // 'Z-foo'
  showInForwardOrder: boolean,
  rows: Array<Record<string, any>>,
  isReport?: boolean
) {
  const header = R.find(R.propEq('key', header_key), headers) || {};
  const key = isReport ? header.reportDataKey : header.key;
  let { comparator } = header;

  if (!comparator) {
    comparator = (row) => {
      const value = row[key];
      if (!value) {
        return '';
      }

      return isNaN(value) ? value : Number(value);
    };
  }

  const sorted = R.sortBy(comparator, rows);
  return showInForwardOrder ? sorted : R.reverse(sorted);
};
export const sumRevenueIfExists = function (
  commerceDataValues: Array<Record<string, any>>,
  grouping: string,
  rows: Array<Record<string, any>>
) {
  return R.map((source) => {
    source.revenue = 0;
    source.revenue_event = 0;
    const finalRed = R.reduce(
      (acc, commerce) => {
        const revenue = Number(commerce.revenue);
        const revenue_event = Number(commerce.total_count);

        if (!isNaN(revenue)) {
          acc.revenue += revenue;
        }

        if (!isNaN(revenue_event)) {
          acc.revenue_event += revenue_event;
        }

        return acc;
      },
      source,
      R.filter((datum) => {
        return source.os === datum.os && source[grouping] === datum[grouping];
      }, commerceDataValues)
    );
    return finalRed;
  }, rows);
};
export const filterListBySearchTerm = function (search_term: string, key: string, rows: Array<Record<string, any>>) {
  if (!search_term) {
    return rows;
  }

  return R.filter((row) => {
    const str = row[key] || '';
    return str.toLowerCase().includes(search_term.toLowerCase());
  }, rows);
};
export const getTotals = function (omit_props: Array<string>, data: Array<Record<string, any>>) {
  return R.reduce(
    (acc, row) => {
      return R.pipe(
        R.omit(omit_props),
        R.mapObjIndexed((val, key) => (acc[key] || 0) + val)
      )(row);
    },
    {},
    data
  );
};
export const getCookie = function (cname: string) {
  var name = cname + '=';
  var ca = document.cookie.split(';');

  for (var i = 0; i < ca.length; i++) {
    var c = ca[i];

    while (c.charAt(0) == ' ') {
      c = c.substring(1);
    }

    if (c.indexOf(name) != -1) {
      return c.substring(name.length, c.length);
    }
  }

  return '';
};
export const setCookie = function (cname: string, cvalue: string, days: number) {
  var expiryDate = new Date();
  expiryDate.setTime(expiryDate.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = cname + '=' + cvalue + '; expires=' + expiryDate.toUTCString() + '; path=/; domain=.branch.io';
};

/*
 * Encodes HTML characters
 * Used when user input is unsafely added to the DOM
 */
export const validateInput = htmlEncoder;

// to allow for multiple dimensions, create array of dimension values
// then serialize, so we can use this value as an object key
function joinDimensionsAndSerialize(dimensions, event) {
  const joinDimensions = R.map((dimension) => {
    // hack for click data source
    const mappedDimensionName = FILTER_DIMENSIONS[dimension];
    const eventDimension = event[mappedDimensionName] || event[dimension] || null;
    return {
      [dimension]: eventDimension,
    };
  }, dimensions);
  return JSON.stringify(joinDimensions);
}

// Check './__tests__/stubs/druid-groupby-data.json' for an example druidData input
// Check './__tests__/stubs/processed-table-data.json' for an example output
export const processGroupbyTableData = (
  druidData: Record<string, any>,
  sources: Array<string>,
  dimensions: Array<string>
) => {
  if (R.isNil(druidData)) {
    return [];
  }

  const newObj = {};
  // used for zero filling columns
  const columnTypes = {};

  if (R.isEmpty(dimensions)) {
    // allow for no dimension
    dimensions = [''];
  }

  // iterate over data sources
  for (var source in druidData) {
    if (!druidData.hasOwnProperty(source)) {
      continue;
    }

    var sourceResult = druidData[source];

    if (!Array.isArray(sourceResult) || R.isEmpty(sourceResult)) {
      continue;
    }

    const revColumn = source === 'eo_commerce_event';
    // iterate over data for each source
    sourceResult.forEach((result) => {
      const { event } = result;

      if (event.name) {
        // for eo_custom_event and eo_commerce_event
        // need to pull out specific event name
        source = event.name;
      }

      // zero fill if value doesn't exist
      columnTypes[source] = 0;

      if (revColumn) {
        columnTypes.revenue = 0;
      }

      if (source === 'click') {
        // translate click platform
        for (var key in event) {
          if (!event.hasOwnProperty(key)) {
            continue;
          }

          if (PLATFORMS[key]) {
            event[PLATFORMS[key]] = PLATFORMS[event[key]];
            delete event[key];
          }
        }
      }

      const serializedDimensions = joinDimensionsAndSerialize(dimensions, event);
      // need previous total count for calculating averages hours to install
      // and for totaling the count for each dimension set
      const previousCount = (newObj[serializedDimensions] && newObj[serializedDimensions][source]) || 0;
      // calculate hours from last event if available
      const lastHourValue = event.hours_from_last_attributed_touch_to_event;
      let lastHourJoinDimensions = {};

      if (!R.isNil(lastHourValue)) {
        const columnHoursFromEvent = source + '_hours_from_last_attributed_touch_to_event';
        const previousLastHourAverage =
          (newObj[serializedDimensions] && newObj[serializedDimensions][columnHoursFromEvent]) || 0;
        // updateLastHourAverage = previous total hours + current total hours / total count
        const updateLastHourAverage =
          (previousLastHourAverage * previousCount + +lastHourValue * event.total_count) /
          (previousCount + event.total_count);
        lastHourJoinDimensions = {
          [columnHoursFromEvent]: updateLastHourAverage,
        };
      }

      // calculate purchase revenue column amounts
      let purchaseRevenue = {};

      if (revColumn) {
        const previousRevenue = (newObj[serializedDimensions] && newObj[serializedDimensions].revenue) || 0;
        purchaseRevenue = {
          revenue: +event.revenue + previousRevenue,
        };
      }

      // merge source values for dimension
      newObj[serializedDimensions] = R.mergeAll([
        newObj[serializedDimensions] || {},
        {
          [source]: event.total_count + previousCount,
        },
        purchaseRevenue,
        lastHourJoinDimensions,
      ]);
    });
  }

  // build final table array by adding dimensions
  const newArr = R.reduceObj(
    (acc, val, key) => {
      const deserialized = JSON.parse(key);
      const dimensions = R.reduce(
        (acc, curr) => {
          return R.merge(acc, curr);
        },
        {},
        deserialized
      );
      // columnTypes is used to place 0 for values that don't exist
      const obj = R.mergeAll([columnTypes, val, dimensions]);
      acc.push(obj);
      return acc;
    },
    [],
    newObj
  );
  return newArr;
};

/* -----------------------------------------------------------
TODO: Add endpoint in subscription service to fetch tier ids
------------------------------------------------------------ */
export function dynamicallyAddTiers(productConfig: Record<string, any>, defaultTiers: Array<Record<string, any>>) {
  productConfig.tiers = defaultTiers;
  return productConfig;
}
export function convertToChron(data: Record<string, any>) {
  // format: minute hour day/month month/year day/week year
  const chronList = [data.minute, data.hour, '*', '*', '*', '*'];

  if (data.frequency === '2') {
    chronList[4] = data.dayweek;
  } else if (data.frequency === '3') {
    chronList[2] = data.daymonth;
  }

  return chronList.join(' ');
}
export function convertFromChron(chron: string) {
  const chronList = chron.split(' ');
  let data = {
    minute: chronList[0],
    hour: chronList[1],
  };

  if (chronList[2] !== '*') {
    data = { ...data, frequency: '3', daymonth: chronList[2] };
  } else if (chronList[4] !== '*') {
    data = { ...data, frequency: '2', daymonth: chronList[4] };
  } else {
    data = { ...data, frequency: '1' };
  }

  return data;
}
export function validateUrl(url: string) {
  var regex = /^(?:\w+:)?\/\/([^\s\.]+\.\S{2}|localhost[\:?\d]*)\S*$/g;
  return regex.test(url);
}

/**
 * JIRA: DEVEX-201
 * Get App Store ID from valid iTunes URL
 * @param {string} url - (e.g https://apps.apple.com/us/app/[APP]/id123456)
 * @return {string} App Store ID (e.g. 123456), else null
 */
export function getAppStoreIdFromUrl(url: string) {
  if (url && url.indexOf('id') > -1 && url.match(/((apps|itunes).apple.com).*id(\d+)/)) {
    var app_store_ids = url.match(/\/id(\d+)/);

    if (app_store_ids && app_store_ids.length > 0) {
      return app_store_ids[0].replace('/id', '');
    }
  }
}

/**
 * Checks if email matches a given domain
 * @param {string} email  - email to check (e.g. user@branch.io)
 * @param {string} domains - list of domains (e.g. ['branch.io','pinterest.com'])
 * @return {boolean}
 */
export function isEmailDomainInList(email: string, domains: Array<string>) {
  if (!email || !domains || R.isEmpty(domains)) {
    return false;
  }

  const emailDomain = email.split('@')[1];
  return R.contains(emailDomain, domains);
}

/**
 * Used to retrieve filter values from url query parameters for analytics
 */
export function parseFiltersFromQueryString(filtersString: string | string[]): Record<string, any> | null | undefined {
  try {
    const filters = JSON.parse(filtersString) || {};
    return Object.keys(filters).reduce((acc, key) => {
      const humanReadable = convertHumanReadableToDruid(key);

      if (humanReadable) {
        acc[humanReadable] = filters[key];
      }

      return acc;
    }, {});
  } catch (e) {
    return;
  }
}

/**
 * truncate a number to n decimal places without rounding (see Number.prototype.toFixed)
 * @param {string} val - number to truncate
 * @param {number} n - number of decimal places to truncate num
 * @return {string}
 */
export function truncToNDecimals(val: string, n: number): string {
  const int = val.split('.')[0];
  const decimals = val.split('.')[1];

  if (decimals && decimals.length > n) {
    // do not trucate when rounding
    val = `${int}.${decimals.substring(0, n)}`;
  }

  return val;
}
export function isMmpEnabled(partners: Array<adPartnerTypes.AdPartner>): boolean {
  return ['a_facebook', 'a_snap', 'a_twitter'].some((mmpMachineName) => {
    return R.path(['enabled'], R.find(R.propEq('machine_name', mmpMachineName), partners));
  });
}
export function generateRandomId() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    var r = parseInt(Math.random() * 16);
    return (c == 'x' ? r : (r & 0x3) | 0x8).toString(16); // eslint-disable-line no-bitwise
  });
}

/*
 * function that returns whether a formatted string represents a numeric value (e.g. a count, percent, monetary amount, etc.)
 *
 * @params {string} val - string representation of a numeric value
 * @return {boolean} whether a string is a numeric representation
 *
 * NOTE: timestamps should return false
 */
export function isNumericString(val: string): boolean {
  return /^(-)?[\d,.%$\s]+$/.test(val);
}
export function flattenObj(
  obj: {},
  options: {
    keepOriginal: boolean;
  }
) {
  function traverse(innerObj: {}) {
    return R.chain(([key, value]) => {
      if (Object.prototype.toString.call(value) === '[object Object]') {
        const flattenedPairs = R.map(([innerKey, innerValue]) => [`${key}.${innerKey}`, innerValue], traverse(value));

        if (options.keepOriginal) {
          flattenedPairs.push([key, value]);
        }

        return flattenedPairs;
      }

      return [[key, value]];
    }, R.toPairs(innerObj));
  }

  return R.fromPairs(traverse(obj));
}

/**
 * Users can accept and then later decline our cookie policy and vice versa.
 * We need to respect the users most recent decision.
 */
export function hasAcceptedCookies(acceptDate: string, declineDate: string): boolean {
  if (!acceptDate) {
    return false;
  }

  if (!declineDate) {
    return true;
  }

  if (new Date(acceptDate) > new Date(declineDate)) {
    return true;
  }

  return false;
}
export function hasValidFileExtension(fileName: string, extensions: Array<string>): boolean {
  if (!fileName || !extensions) {
    return false;
  }

  const extension = fileName.split('.').pop().toLowerCase();
  return extensions.indexOf(extension) !== -1;
}
export const getLoggedInFullName = (firstName: string, lastName: string | null | undefined): string => {
  return `${firstName} ${lastName || ''}`;
};
const IOS_APP_STORE_ID_REGEX = /^id(\d+)$/;
const ANDROID_PACKAGE_NAME_REGEX = /^([a-z0-9_]+)(\.[a-z0-9_]+)+$/i;

/**
 * user has ios app if
 * 1. the ios_app_store_id field is populated or
 * 2. the value saved in iosUrl is matches the ios_app_store_id regex
 * NOTE: we must use the regex test here since the value saved in androidUrl may be an actual url.
 * There is also no way to tell the type of value saved in androidUrl from the ios_app field.
 * returns an ios app id if found in ios_app_store_id or parsed from ios_url. Returns false otherwise
 */
export const getIOSAppId = (iosAppStoreId: string | null | undefined, iosUrl: string | null | undefined) => {
  return iosAppStoreId || (!!iosUrl && !!IOS_APP_STORE_ID_REGEX.test(iosUrl) && iosUrl);
};

/**
 * see hasIOSApp note, identical logic.
 */
export const getAndroidPackageName = (
  androidPackageName: string | null | undefined,
  androidUrl: string | null | undefined
) => {
  return androidPackageName || (!!androidUrl && !!ANDROID_PACKAGE_NAME_REGEX.test(androidUrl) && androidUrl);
};

/**
 * Determines loading status from an arbitrary list of status on reducer state
 * Precedence of state is:
 *   - Any error state for any key results in loading status of error
 *   - Otherwise returns the minimum loading status for all states provided
 */
export const determineLoadingStatus = (state: Record<string, any>, stateKeys: Array<string>) => {
  const statuses = stateKeys.map((key) => state[key]);

  if (statuses.includes(LOADING_STATUS.ERROR)) {
    return LOADING_STATUS.ERROR;
  }

  return Math.min.apply(null, statuses);
};
export const getViewFromQuery = (query: Record<string, string>): View | null | undefined => {
  if (query.app_id) {
    return {
      current_view: 'app',
      app_id: query.app_id,
    };
  }

  if (query.organization_id) {
    return {
      current_view: 'org',
      organization_id: query.organization_id,
    };
  }

  return null;
};
export const truncate = (str: string, num: number) => {
  return str.length > num ? `${str.slice(0, num).trim()}...` : str;
};
export const filterRows = (rows: Array<Record<string, any>>, keyToFilterBy: string, filteredValue: string) => {
  if (!rows) {
    return [];
  }

  if (filteredValue === 'all') {
    return rows;
  }

  return rows.filter((row) => {
    return row[keyToFilterBy] === filteredValue;
  });
};
export const replaceObjectInArray = (
  arr: Array<Record<string, any>>,
  indexToReplace: number,
  newDataObject: Record<string, any>
) => {
  const result = [];

  if (!arr) {
    return result;
  }

  return [...arr.slice(0, indexToReplace), newDataObject, ...arr.slice(indexToReplace + 1)];
};
export const isEnglishString = (string: string) => {
  const englishStringRegex = /^[a-zA-Z0-9$@$!%*?&#^-_. +\-\[\]]+$/;

  if (string) {
    return englishStringRegex.test(string);
  }

  return false;
};
export const formatNumberToUsdString = (num: number) => {
  return num.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
  });
};
export const compareAlphaNumericChars = (a: string, b: string) =>
  a.localeCompare(b, undefined, {
    numeric: true,
    sensitivity: 'base',
  });
